/**
 * Liquidity Sweep Pro — detección de barridos de liquidez con scoring 0-100.
 *
 * Puerto fiel a Pine Script v6 v1.3.1. Motor JS duplicado del backend
 * para garantizar mismos resultados con mismos datos.
 */

import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'

// ── Constants ────────────────────────────────────────────────────────────────
const MAX_STORED = 25
const FORM_LEN = 10

const RISK_PRESETS = {
  Conservative: [0.50, 1.0, 2.0, 4.0],
  Balanced:     [0.25, 1.0, 2.0, 3.0],
  Aggressive:   [0.15, 1.5, 2.5, 4.0],
  Scalping:     [0.10, 0.8, 1.5, 2.0],
  Custom:       [0.25, 1.0, 2.0, 3.0],
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcATR(candles, period) {
  const n = candles.length
  const tr = candles.map((c, i) =>
    i === 0
      ? c.high - c.low
      : Math.max(c.high - c.low, Math.abs(c.high - candles[i - 1].close), Math.abs(c.low - candles[i - 1].close))
  )
  const out = new Array(n).fill(null)
  if (n < period) return out
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  out[period - 1] = sum / period
  for (let i = period; i < n; i++) out[i] = (out[i - 1] * (period - 1) + tr[i]) / period
  return out
}

function calcSMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let s = 0, cnt = 0
    for (let j = i - len + 1; j <= i; j++) {
      if (arr[j] != null && !Number.isNaN(arr[j])) { s += arr[j]; cnt++ }
    }
    if (cnt > 0) out[i] = s / cnt
  }
  return out
}

function calcEMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  const k = 2 / (len + 1)
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] == null || Number.isNaN(arr[i])) continue
    if (out[i - 1] == null) {
      let sum = 0, cnt = 0
      for (let j = Math.max(0, i - len + 1); j <= i; j++) {
        if (arr[j] != null && !Number.isNaN(arr[j])) { sum += arr[j]; cnt++ }
      }
      out[i] = cnt > 0 ? sum / cnt : null
    } else {
      out[i] = arr[i] * k + out[i - 1] * (1 - k)
    }
  }
  return out
}

function safe(value, fallback = 0.0) {
  if (value == null || Number.isNaN(value) || !Number.isFinite(value)) return fallback
  return value
}

function clamp(value, lo, hi) {
  return Math.max(lo, Math.min(hi, value))
}

function findPivotHigh(high, left, right, idx) {
  if (idx - left < 0 || idx + right >= high.length) return false
  const val = high[idx]
  let max = -Infinity
  for (let i = idx - left; i <= idx + right; i++) max = Math.max(max, high[i])
  return val === max && Number.isFinite(val)
}

function findPivotLow(low, left, right, idx) {
  if (idx - left < 0 || idx + right >= low.length) return false
  const val = low[idx]
  let min = Infinity
  for (let i = idx - left; i <= idx + right; i++) min = Math.min(min, low[i])
  return val === min && Number.isFinite(val)
}

function score(direction, level, open, high, low, close, atr, volComp, htfComp) {
  const rng = high - low
  if (rng <= 0 || atr <= 0) return 0.0

  let wick, reclaim, closePos
  if (direction === 1) {
    wick = Math.min(open, close) - low
    reclaim = close - level
    closePos = (close - low) / rng
  } else {
    wick = high - Math.max(open, close)
    reclaim = level - close
    closePos = 1.0 - (close - low) / rng
  }

  const wickComp = clamp(wick / atr, 0.0, 1.0)
  const rclComp = clamp(reclaim / atr, 0.0, 1.0)
  const cpComp = clamp(closePos, 0.0, 1.0)

  return (wickComp * 0.30 + rclComp * 0.25 + cpComp * 0.20 + volComp * 0.15 + htfComp * 0.10) * 100.0
}

function resolveRiskPreset(cfg) {
  const preset = cfg.riskPreset || 'Balanced'
  let [buf, tp1, tp2, tp3] = RISK_PRESETS[preset] || RISK_PRESETS.Balanced
  if (preset === 'Custom') {
    buf = safe(cfg.slBufferAtr, buf)
    tp1 = safe(cfg.tp1Mult, tp1)
    tp2 = safe(cfg.tp2Mult, tp2)
    tp3 = safe(cfg.tp3Mult, tp3)
  }
  return [buf, tp1, tp2, tp3]
}

function nearestLiquidity(direction, entry, hiLvl, hiUsed, loLvl, loUsed) {
  if (direction === 1) {
    let best = null
    for (let i = 0; i < hiLvl.length; i++) {
      if (!hiUsed[i] && hiLvl[i] > entry) {
        if (best == null || hiLvl[i] < best) best = hiLvl[i]
      }
    }
    return best
  } else {
    let best = null
    for (let i = 0; i < loLvl.length; i++) {
      if (!loUsed[i] && loLvl[i] < entry) {
        if (best == null || loLvl[i] > best) best = loLvl[i]
      }
    }
    return best
  }
}

function emptyResult() {
  return {
    signals: [],
    activeTrade: null,
    lastSignal: null,
    sweeps: [],
    restingLiquidity: { bsl: [], ssl: [] },
    equalLevels: { eqh: [], eql: [] },
    dashboard: {
      trend: 'Neutral',
      htfBias: '—',
      signal: 'Wait',
      lastSweep: '—',
      timeframe: '—',
      sl: '—',
      tp1: '—',
      tp2: '—',
      tp3: '—',
      rr: '—',
      slDistPct: '—',
      trades: 0,
      wl: '—',
      winRate: '—',
      form: '—',
    },
    stats: { win: 0, loss: 0, closed: 0 },
  }
}

// ── Detect ───────────────────────────────────────────────────────────────────

function detect(candles, config, htfCandles) {
  const cfg = { ...defaultConfig, ...(config || {}) }
  if (!candles || candles.length < 10) return emptyResult()

  const n = candles.length
  const time = candles.map(c => c.time)
  const open = candles.map(c => c.open)
  const high = candles.map(c => c.high)
  const low = candles.map(c => c.low)
  const close = candles.map(c => c.close)
  const volume = candles.map(c => c.volume ?? 0)

  const swingLen = Math.max(3, parseInt(cfg.swingLength, 10))
  const maxSweepDist = Math.max(5, parseInt(cfg.maxSweepDistance, 10))
  const minScore = clamp(parseFloat(cfg.minSweepScore), 0, 100)
  const requireChoch = !!cfg.requireChoch
  const minorLen = Math.max(2, parseInt(cfg.structurePivotLength, 10))
  const confirmWindow = Math.max(1, parseInt(cfg.confirmationWindow, 10))
  const useVolume = !!cfg.useVolumeFilter
  const volLen = Math.max(2, parseInt(cfg.volumeMaLength, 10))
  const volMult = Math.max(1.1, parseFloat(cfg.volumeSpikeMult))
  const useHtf = !!cfg.useHtfBias
  const htfEmaLen = Math.max(5, parseInt(cfg.htfEmaLength, 10))
  const atrLen = Math.max(5, parseInt(cfg.atrLength, 10))
  const useBe = !!cfg.breakEvenAfterTp1
  const showEq = !!cfg.showEqualHighsLows
  const eqTol = Math.max(0.001, parseFloat(cfg.equalToleranceAtr))
  const maxLiq = Math.max(1, parseInt(cfg.maxLiquidityLevels, 10))

  const warmupBars = Math.max(swingLen * 2, 50)
  if (n < warmupBars + 1) return emptyResult()

  const atrs = calcATR(candles, atrLen)
  const volSmas = calcSMA(volume, volLen)

  let htfValid = false, htfBull = false, htfBear = false
  if (useHtf && htfCandles && htfCandles.length >= htfEmaLen + 2) {
    const htfClose = htfCandles.map(c => c.close)
    const htfEma = calcEMA(htfClose, htfEmaLen)
    const prevClose = htfClose[htfClose.length - 2]
    const prevEma = htfEma[htfEma.length - 2]
    if (prevClose != null && prevEma != null) {
      htfValid = true
      htfBull = prevClose > prevEma
      htfBear = prevClose < prevEma
    }
  }

  const hiLvl = []
  const hiBar = []
  const hiUsed = []
  const loLvl = []
  const loBar = []
  const loUsed = []
  const eqhLevels = []
  const eqlLevels = []

  let lastMinorHigh = null
  let lastMinorLow = null

  let pendingDir = 0
  let pendingStartBar = -1
  let pendingLvl = null
  let pendingWick = null
  let pendingScore = null

  let activeEntry = null
  let activeSL = null
  let activeTP1 = null
  let activeTP2 = null
  let activeTP3 = null
  let activeDir = 0
  let entryBar = -1

  let tp1Reached = false
  let tp2Reached = false
  let tp3Reached = false
  let beActive = false

  let statWins = 0
  let statLosses = 0
  let formStr = ''

  let sweepTrend = 0
  let lastSigType = '—'
  let lastSigScore = null

  const signals = []
  const sweeps = []

  for (let i = 0; i < n; i++) {
    let atr = safe(atrs[i], 1e-9)
    if (atr <= 1e-9) atr = 1e-9

    const hasVolume = volume.some(v => v > 0)
    const volSmaVal = safe(volSmas[i])
    const volRatio = hasVolume && volSmaVal > 0 ? volume[i] / volSmaVal : null
    const volComp = useVolume && volRatio != null
      ? clamp((volRatio - 1.0) / Math.max(volMult - 1.0, 0.1), 0.0, 1.0)
      : 0.5

    const htfCompBull = !useHtf ? 0.5 : (htfBull ? 1.0 : 0.0)
    const htfCompBear = !useHtf ? 0.5 : (htfBear ? 1.0 : 0.0)

    const isWarmedUp = i >= warmupBars

    // Swing pivots
    const pivHighCenter = i - swingLen
    if (pivHighCenter >= swingLen && pivHighCenter + swingLen < n) {
      if (findPivotHigh(high, swingLen, swingLen, pivHighCenter)) {
        const lvl = high[pivHighCenter]
        const bar = pivHighCenter
        hiLvl.unshift(lvl)
        hiBar.unshift(bar)
        hiUsed.unshift(false)

        if (showEq && hiLvl.length >= 2) {
          const prevH = hiLvl[1]
          const prevBar = hiBar[1]
          if (Math.abs(lvl - prevH) <= atr * eqTol) {
            eqhLevels.push({ from: prevBar, to: bar, level: Math.max(lvl, prevH) })
          }
        }

        if (hiLvl.length > MAX_STORED) { hiLvl.pop(); hiBar.pop(); hiUsed.pop() }
      }
    }

    const pivLowCenter = i - swingLen
    if (pivLowCenter >= swingLen && pivLowCenter + swingLen < n) {
      if (findPivotLow(low, swingLen, swingLen, pivLowCenter)) {
        const lvl = low[pivLowCenter]
        const bar = pivLowCenter
        loLvl.unshift(lvl)
        loBar.unshift(bar)
        loUsed.unshift(false)

        if (showEq && loLvl.length >= 2) {
          const prevL = loLvl[1]
          const prevBar = loBar[1]
          if (Math.abs(lvl - prevL) <= atr * eqTol) {
            eqlLevels.push({ from: prevBar, to: bar, level: Math.min(lvl, prevL) })
          }
        }

        if (loLvl.length > MAX_STORED) { loLvl.pop(); loBar.pop(); loUsed.pop() }
      }
    }

    // Minor structure pivots
    const minorHighCenter = i - minorLen
    if (minorHighCenter >= minorLen && minorHighCenter + minorLen < n) {
      if (findPivotHigh(high, minorLen, minorLen, minorHighCenter)) {
        lastMinorHigh = high[minorHighCenter]
      }
    }
    const minorLowCenter = i - minorLen
    if (minorLowCenter >= minorLen && minorLowCenter + minorLen < n) {
      if (findPivotLow(low, minorLen, minorLen, minorLowCenter)) {
        lastMinorLow = low[minorLowCenter]
      }
    }

    // Sweep detection
    let bullSweep = false
    let bullLvl = null
    let bullLvlBar = null
    let bearSweep = false
    let bearLvl = null
    let bearLvlBar = null

    for (let j = loLvl.length - 1; j >= 0; j--) {
      if (!loUsed[j]) {
        const lvl = loLvl[j]
        const bar = loBar[j]
        if (i - bar <= maxSweepDist) {
          if (close[i] < lvl) {
            loUsed[j] = true
          } else if (low[i] < lvl && close[i] > lvl) {
            loUsed[j] = true
            if (!bullSweep) {
              bullSweep = true
              bullLvl = lvl
              bullLvlBar = bar
            }
          }
        }
      }
    }

    for (let j = hiLvl.length - 1; j >= 0; j--) {
      if (!hiUsed[j]) {
        const lvl = hiLvl[j]
        const bar = hiBar[j]
        if (i - bar <= maxSweepDist) {
          if (close[i] > lvl) {
            hiUsed[j] = true
          } else if (high[i] > lvl && close[i] < lvl) {
            hiUsed[j] = true
            if (!bearSweep) {
              bearSweep = true
              bearLvl = lvl
              bearLvlBar = bar
            }
          }
        }
      }
    }

    const bullScore = bullSweep && bullLvl != null
      ? score(1, bullLvl, open[i], high[i], low[i], close[i], atr, volComp, htfCompBull)
      : 0.0
    const bearScore = bearSweep && bearLvl != null
      ? score(-1, bearLvl, open[i], high[i], low[i], close[i], atr, volComp, htfCompBear)
      : 0.0

    const bullQ = bullSweep && bullScore >= minScore && isWarmedUp
    const bearQ = bearSweep && bearScore >= minScore && isWarmedUp

    if (bullQ && bullLvl != null) {
      sweeps.push({ time: time[i], idx: i, dir: 1, level: bullLvl, levelBar: bullLvlBar, score: bullScore, wick: low[i] })
    }
    if (bearQ && bearLvl != null) {
      sweeps.push({ time: time[i], idx: i, dir: -1, level: bearLvl, levelBar: bearLvlBar, score: bearScore, wick: high[i] })
    }

    // Signal resolution
    let fireBull = false
    let fireBear = false
    let sigScore = null
    let anchorExtreme = null
    let sigLvl = null

    if (requireChoch) {
      if (pendingDir !== 0 && (i - pendingStartBar) > confirmWindow) pendingDir = 0
      if (bullQ && bullLvl != null) {
        pendingDir = 1
        pendingStartBar = i
        pendingLvl = bullLvl
        pendingWick = low[i]
        pendingScore = bullScore
      }
      if (bearQ && bearLvl != null) {
        pendingDir = -1
        pendingStartBar = i
        pendingLvl = bearLvl
        pendingWick = high[i]
        pendingScore = bearScore
      }
      if (pendingDir === 1 && lastMinorHigh != null && close[i] > lastMinorHigh) {
        fireBull = true
        sigScore = pendingScore
        anchorExtreme = pendingWick
        sigLvl = pendingLvl
        pendingDir = 0
      }
      if (pendingDir === -1 && lastMinorLow != null && close[i] < lastMinorLow) {
        fireBear = true
        sigScore = pendingScore
        anchorExtreme = pendingWick
        sigLvl = pendingLvl
        pendingDir = 0
      }
    } else {
      if (bullQ && bullLvl != null) {
        fireBull = true
        sigScore = bullScore
        anchorExtreme = low[i]
        sigLvl = bullLvl
      }
      if (bearQ && bearLvl != null) {
        fireBear = true
        sigScore = bearScore
        anchorExtreme = high[i]
        sigLvl = bearLvl
      }
    }

    // Risk engine
    const [buf, tp1Mult, tp2Mult, tp3Mult] = resolveRiskPreset(cfg)

    const sigConflict = fireBull && fireBear
    const canOpen = activeDir === 0
    const openLong = fireBull && !sigConflict && atr > 0 && canOpen
    const openShort = fireBear && !sigConflict && atr > 0 && canOpen

    if (openLong && anchorExtreme != null) {
      activeEntry = close[i]
      let rawSL = anchorExtreme - atr * buf
      let dist = Math.abs(activeEntry - rawSL)
      if (dist < atr * 0.5) {
        rawSL = activeEntry - atr * 0.5
        dist = atr * 0.5
      }
      activeSL = rawSL
      activeTP1 = activeEntry + dist * tp1Mult
      activeTP2 = activeEntry + dist * tp2Mult
      activeTP3 = activeEntry + dist * tp3Mult
      activeDir = 1
      entryBar = i
      tp1Reached = false
      tp2Reached = false
      tp3Reached = false
      beActive = false
      sweepTrend = 1

      signals.push({
        time: time[i],
        idx: i,
        dir: 1,
        entry: activeEntry,
        sl: activeSL,
        tp1: activeTP1,
        tp2: activeTP2,
        tp3: activeTP3,
        score: sigScore,
        sweepLevel: sigLvl,
        sweepWick: anchorExtreme,
        chochConfirmed: requireChoch,
        htfAligned: useHtf ? htfBull : null,
        liquidityTarget: nearestLiquidity(1, activeEntry, hiLvl, hiUsed, loLvl, loUsed),
        result: 'active',
        pnl: null,
      })
    }

    if (openShort && anchorExtreme != null) {
      activeEntry = close[i]
      let rawSL = anchorExtreme + atr * buf
      let dist = Math.abs(rawSL - activeEntry)
      if (dist < atr * 0.5) {
        rawSL = activeEntry + atr * 0.5
        dist = atr * 0.5
      }
      activeSL = rawSL
      activeTP1 = activeEntry - dist * tp1Mult
      activeTP2 = activeEntry - dist * tp2Mult
      activeTP3 = activeEntry - dist * tp3Mult
      activeDir = -1
      entryBar = i
      tp1Reached = false
      tp2Reached = false
      tp3Reached = false
      beActive = false
      sweepTrend = -1

      signals.push({
        time: time[i],
        idx: i,
        dir: -1,
        entry: activeEntry,
        sl: activeSL,
        tp1: activeTP1,
        tp2: activeTP2,
        tp3: activeTP3,
        score: sigScore,
        sweepLevel: sigLvl,
        sweepWick: anchorExtreme,
        chochConfirmed: requireChoch,
        htfAligned: useHtf ? htfBear : null,
        liquidityTarget: nearestLiquidity(-1, activeEntry, hiLvl, hiUsed, loLvl, loUsed),
        result: 'active',
        pnl: null,
      })
    }

    // Trade management
    const canCheckHit = activeDir !== 0 && entryBar >= 0 && i > entryBar
    if (canCheckHit && activeEntry != null && activeSL != null) {
      const slHit = (activeDir === 1 && low[i] <= activeSL) || (activeDir === -1 && high[i] >= activeSL)
      const tp1Hit = (activeDir === 1 && high[i] >= activeTP1) || (activeDir === -1 && low[i] <= activeTP1)
      const tp2Hit = (activeDir === 1 && high[i] >= activeTP2) || (activeDir === -1 && low[i] <= activeTP2)
      const tp3Hit = (activeDir === 1 && high[i] >= activeTP3) || (activeDir === -1 && low[i] <= activeTP3)

      const tp1First = tp1Hit && !tp1Reached && !slHit
      const tp2First = tp2Hit && !tp2Reached && !slHit
      const tp3First = tp3Hit && !tp3Reached && !slHit

      if (tp1First) tp1Reached = true
      if (tp2First) tp2Reached = true
      if (tp3First) tp3Reached = true

      if (useBe && tp1First && !beActive) {
        activeSL = activeEntry
        beActive = true
      }

      if (slHit) {
        const sig = signals[signals.length - 1]
        if (tp1Reached) {
          statWins += 1
          formStr += '▰'
          if (sig && sig.result === 'active') {
            sig.result = 'win'
            sig.pnl = 0.0
            sig.closeTime = time[i]
            sig.tp1Hit = true
            sig.tp2Hit = tp2Reached
            sig.tp3Hit = tp3Reached
          }
        } else {
          statLosses += 1
          formStr += '▱'
          const pnl = activeDir === 1 ? activeSL - activeEntry : activeEntry - activeSL
          if (sig && sig.result === 'active') {
            sig.result = 'loss'
            sig.pnl = pnl
            sig.closeTime = time[i]
          }
        }
        if (formStr.length > FORM_LEN) formStr = formStr.slice(-FORM_LEN)
        activeEntry = activeSL = activeTP1 = activeTP2 = activeTP3 = null
        activeDir = 0
        entryBar = -1
        tp1Reached = tp2Reached = tp3Reached = false
        beActive = false
      } else if (tp3First) {
        statWins += 1
        formStr += '▰'
        if (formStr.length > FORM_LEN) formStr = formStr.slice(-FORM_LEN)
        const pnl = activeDir === 1 ? activeTP3 - activeEntry : activeEntry - activeTP3
        const sig = signals[signals.length - 1]
        if (sig && sig.result === 'active') {
          sig.result = 'win'
          sig.pnl = pnl
          sig.closeTime = time[i]
          sig.tp1Hit = tp1Reached
          sig.tp2Hit = tp2Reached
          sig.tp3Hit = true
        }
        activeEntry = activeSL = activeTP1 = activeTP2 = activeTP3 = null
        activeDir = 0
        entryBar = -1
        tp1Reached = tp2Reached = tp3Reached = false
        beActive = false
      }
    }

    if (fireBull) { lastSigType = 'Bullish'; lastSigScore = sigScore }
    if (fireBear) { lastSigType = 'Bearish'; lastSigScore = sigScore }
  }

  // Resting liquidity snapshot
  const restingBsl = []
  const restingSsl = []
  for (let idx = 0; idx < hiLvl.length; idx++) {
    if (!hiUsed[idx] && restingBsl.length < maxLiq) restingBsl.push({ level: hiLvl[idx], bar: hiBar[idx] })
  }
  for (let idx = 0; idx < loLvl.length; idx++) {
    if (!loUsed[idx] && restingSsl.length < maxLiq) restingSsl.push({ level: loLvl[idx], bar: loBar[idx] })
  }

  const lastSignal = signals.length ? signals[signals.length - 1] : null
  let activeTrade = null
  if (activeDir !== 0 && activeEntry != null) {
    activeTrade = {
      dir: activeDir,
      entry: activeEntry,
      sl: activeSL,
      tp1: activeTP1,
      tp2: activeTP2,
      tp3: activeTP3,
      tp1Hit: tp1Reached,
      tp2Hit: tp2Reached,
      tp3Hit: tp3Reached,
      beActive,
      time: entryBar >= 0 ? time[entryBar] : null,
    }
  }

  const trendWord = sweepTrend === 1 ? 'Bullish' : sweepTrend === -1 ? 'Bearish' : 'Neutral'
  const biasStr = !useHtf ? 'Off' : (htfBull ? 'Bullish' : htfBear ? 'Bearish' : '—')
  const signalStr = activeDir === 1 ? 'LONG' : activeDir === -1 ? 'SHORT' : 'Wait'
  let lastSweepStr = lastSigType !== '—' ? lastSigType : '—'
  if (lastSigScore != null) lastSweepStr += ` ${lastSigScore.toFixed(0)}`

  let rrStr = '—'
  let slDistPctStr = '—'
  if (activeDir !== 0 && activeEntry != null && activeSL != null) {
    const risk = Math.abs(activeEntry - activeSL)
    if (risk > 0 && activeTP1 != null) {
      const rr = Math.abs(activeTP1 - activeEntry) / risk
      rrStr = `1:${rr.toFixed(2)}`
    }
    slDistPctStr = `${(risk / activeEntry * 100).toFixed(2)}%`
  }

  const closedTrades = statWins + statLosses
  const winRateStr = closedTrades > 0 ? `${(statWins / closedTrades * 100).toFixed(1)}%` : '—'

  const dashboard = {
    trend: trendWord,
    htfBias: biasStr,
    signal: signalStr,
    lastSweep: lastSweepStr,
    timeframe: cfg.htfTimeframe || '—',
    sl: activeSL != null ? activeSL.toFixed(4) : '—',
    tp1: activeTP1 != null ? activeTP1.toFixed(4) : '—',
    tp2: activeTP2 != null ? activeTP2.toFixed(4) : '—',
    tp3: activeTP3 != null ? activeTP3.toFixed(4) : '—',
    rr: rrStr,
    slDistPct: slDistPctStr,
    trades: closedTrades,
    wl: `${statWins} / ${statLosses}`,
    winRate: winRateStr,
    form: formStr.slice(-FORM_LEN) || '—',
  }

  return {
    signals,
    activeTrade,
    lastSignal,
    sweeps,
    restingLiquidity: { bsl: restingBsl, ssl: restingSsl },
    equalLevels: { eqh: eqhLevels, eql: eqlLevels },
    dashboard,
    stats: { win: statWins, loss: statLosses, closed: closedTrades },
  }
}

// ── Render ───────────────────────────────────────────────────────────────────

function render(chart, candleSeries, result, config, api) {
  if (!result) return

  const {
    sweeps = [],
    signals = [],
    restingLiquidity = { bsl: [], ssl: [] },
    equalLevels = { eqh: [], eql: [] },
    activeTrade,
  } = result

  const {
    showRawSweepMarks = true,
    showSweptExtremumLine = true,
    showSweepPenetration = true,
    showSignals = true,
    showSweptLevelLine = true,
    showRestingLiquidity = true,
    showEqualHighsLows = true,
    showLiquidityTarget = true,
    bullColor = '#00E676',
    bearColor = '#FF5252',
    levelColor = '#B0BEC5',
    eqColor = '#B0BEC5',
    liqColor = '#90A4AE',
    targetColor = '#FFCA28',
    entryColor = '#5C8AAE',
    slColor = '#E57373',
    tpColor = '#66BB6A',
    labelOffset = 20,
  } = config

  const common = {
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
  }

  const allBars = candleSeries.data()
  const lastBar = allBars.length ? allBars[allBars.length - 1] : null
  const prevBar = allBars.length > 1 ? allBars[allBars.length - 2] : null
  const barInterval = (lastBar && prevBar && typeof lastBar.time === 'number' && typeof prevBar.time === 'number')
    ? Math.max(lastBar.time - prevBar.time, 1)
    : 3600

  // lastBar.time es string 'YYYY-MM-DD' en timeframes diarios+: sumarle un
  // número concatena en vez de sumar ("2026-07-12" + 72000 = "2026-07-1272000"),
  // lo que lightweight-charts rechaza con un error síncrono que tira toda la
  // app. Sin aritmética de fechas fiable para ese formato, no extendemos la
  // línea más allá de la última vela.
  const lineEndTime = lastBar
    ? (typeof lastBar.time === 'number' ? lastBar.time + barInterval * labelOffset : lastBar.time)
    : null

  const drawLevelLine = (price, color, width, style, startTime) => {
    if (startTime == null || price == null || !Number.isFinite(price) || lineEndTime == null) return
    const series = api.addSeries(c => c.addLineSeries({ ...common, color, lineWidth: width, lineStyle: style }))
    if (series) series.setData([{ time: startTime, value: price }, { time: lineEndTime, value: price }])
  }

  // Raw sweep marks + swept extremum line + penetration
  const sweepsToRender = sweeps.slice(-40)
  for (const sw of sweepsToRender) {
    const color = sw.dir === 1 ? bullColor : bearColor
    if (showRawSweepMarks) {
      api.addMarker({
        time: sw.time,
        position: sw.dir === 1 ? 'belowBar' : 'aboveBar',
        color,
        shape: 'xCross',
        size: 1,
      })
    }
    if (showSweptExtremumLine && sw.levelBar != null) {
      const series = api.addSeries(c => c.addLineSeries({
        ...common,
        color,
        lineWidth: 1,
        lineStyle: 2,
      }))
      if (series) {
        series.setData([
          { time: allBars[sw.levelBar]?.time ?? sw.time, value: sw.level },
          { time: typeof sw.time === 'number' ? sw.time + barInterval * 8 : sw.time, value: sw.level },
        ])
      }
    }
    if (showSweepPenetration) {
      const series = api.addSeries(c => c.addLineSeries({
        ...common,
        color,
        lineWidth: 3,
        lineStyle: 0,
      }))
      if (series) {
        series.setData([
          { time: sw.time, value: sw.level },
          { time: sw.time, value: sw.wick },
        ])
      }
    }
  }

  // Signals markers
  const signalsToRender = signals.slice(-40)
  if (showSignals) {
    for (const sig of signalsToRender) {
      api.addLabel({
        time: sig.time,
        text: sig.dir === 1 ? 'Long ▲' : 'Short ▼',
        color: sig.dir === 1 ? bullColor : bearColor,
        isBull: sig.dir === 1,
        fallbackPrice: sig.entry,
      })
      api.addMarker({
        time: sig.time,
        position: sig.dir === 1 ? 'belowBar' : 'aboveBar',
        color: sig.dir === 1 ? bullColor : bearColor,
        shape: sig.dir === 1 ? 'arrowUp' : 'arrowDown',
        size: 1,
      })
    }
  }

  // Active trade / last signal levels
  const levelsSource = activeTrade || (showSweptLevelLine ? signalsToRender[signalsToRender.length - 1] : null)
  if (levelsSource && (showSweptLevelLine || activeTrade)) {
    const { entry, sl, tp1, tp2, tp3 } = levelsSource
    const startTime = levelsSource.time
    drawLevelLine(entry, entryColor, 1, 2, startTime)
    drawLevelLine(sl, slColor, 2, 0, startTime)
    drawLevelLine(tp1, tpColor, 1, levelsSource.tp1Hit ? 0 : 2, startTime)
    drawLevelLine(tp2, tpColor, 1, levelsSource.tp2Hit ? 0 : 2, startTime)
    drawLevelLine(tp3, tpColor, 1, levelsSource.tp3Hit ? 0 : 2, startTime)

    // Labels con %
    const pct = price => entry ? ((price - entry) / entry) * 100 : 0
    const fmt = v => Number.isFinite(v) ? v.toFixed(1) : '—'
    const fmtPct = v => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`

    api.addLabel({ barsFromLast: labelOffset, price: entry, boxStyle: 'flag', color: entryColor, text: `ENTRY ${fmt(entry)}` })
    api.addLabel({ barsFromLast: labelOffset, price: sl, boxStyle: 'flag', color: slColor, text: `SL ${fmt(sl)} (${fmtPct(pct(sl))})` })
    api.addLabel({ barsFromLast: labelOffset, price: tp1, boxStyle: 'flag', color: tpColor, text: `TP1${levelsSource.tp1Hit ? ' ✓' : ''} ${fmt(tp1)} (${fmtPct(pct(tp1))})` })
    api.addLabel({ barsFromLast: labelOffset, price: tp2, boxStyle: 'flag', color: tpColor, text: `TP2${levelsSource.tp2Hit ? ' ✓' : ''} ${fmt(tp2)} (${fmtPct(pct(tp2))})` })
    api.addLabel({ barsFromLast: labelOffset, price: tp3, boxStyle: 'flag', color: tpColor, text: `TP3${levelsSource.tp3Hit ? ' ✓' : ''} ${fmt(tp3)} (${fmtPct(pct(tp3))})` })
  }

  // Resting liquidity BSL/SSL
  if (showRestingLiquidity) {
    for (const bsl of restingLiquidity.bsl) {
      const series = api.addSeries(c => c.addLineSeries({ ...common, color: liqColor, lineWidth: 1, lineStyle: 2 }))
      if (series) series.setData([{ time: allBars[bsl.bar]?.time ?? lineEndTime, value: bsl.level }, { time: lineEndTime, value: bsl.level }])
      api.addLabel({ barsFromLast: labelOffset, price: bsl.level, boxStyle: 'flag', color: liqColor, text: 'BSL' })
    }
    for (const ssl of restingLiquidity.ssl) {
      const series = api.addSeries(c => c.addLineSeries({ ...common, color: liqColor, lineWidth: 1, lineStyle: 2 }))
      if (series) series.setData([{ time: allBars[ssl.bar]?.time ?? lineEndTime, value: ssl.level }, { time: lineEndTime, value: ssl.level }])
      api.addLabel({ barsFromLast: labelOffset, price: ssl.level, boxStyle: 'flag', color: liqColor, text: 'SSL' })
    }
  }

  // EQH/EQL
  if (showEqualHighsLows) {
    for (const eq of equalLevels.eqh) {
      const series = api.addSeries(c => c.addLineSeries({ ...common, color: eqColor, lineWidth: 1, lineStyle: 0 }))
      if (series) series.setData([{ time: allBars[eq.from]?.time ?? eq.from, value: eq.level }, { time: allBars[eq.to]?.time ?? eq.to, value: eq.level }])
      api.addLabel({ time: allBars[eq.to]?.time ?? eq.to, text: 'EQH', color: eqColor, isBull: true, fallbackPrice: eq.level })
    }
    for (const eq of equalLevels.eql) {
      const series = api.addSeries(c => c.addLineSeries({ ...common, color: eqColor, lineWidth: 1, lineStyle: 0 }))
      if (series) series.setData([{ time: allBars[eq.from]?.time ?? eq.from, value: eq.level }, { time: allBars[eq.to]?.time ?? eq.to, value: eq.level }])
      api.addLabel({ time: allBars[eq.to]?.time ?? eq.to, text: 'EQL', color: eqColor, isBull: false, fallbackPrice: eq.level })
    }
  }

  // Liquidity target
  if (showLiquidityTarget && levelsSource && levelsSource.liquidityTarget != null) {
    const series = api.addSeries(c => c.addLineSeries({
      ...common,
      color: targetColor,
      lineWidth: 1,
      lineStyle: 2,
    }))
    if (series) series.setData([{ time: levelsSource.time, value: levelsSource.liquidityTarget }, { time: lineEndTime, value: levelsSource.liquidityTarget }])
    api.addLabel({ barsFromLast: labelOffset, price: levelsSource.liquidityTarget, boxStyle: 'flag', color: targetColor, text: '◎ Liquidity Target' })
  }
}

// ── Default config ───────────────────────────────────────────────────────────

const defaultConfig = {
  _version: '1.3.1',
  // Main
  swingLength: 21,
  maxSweepDistance: 80,
  minSweepScore: 50,
  // Confirmation
  requireChoch: true,
  structurePivotLength: 8,
  confirmationWindow: 13,
  // Filters
  useVolumeFilter: true,
  volumeMaLength: 21,
  volumeSpikeMult: 1.5,
  useHtfBias: true,
  htfTimeframe: '240',
  htfEmaLength: 50,
  // Risk
  riskPreset: 'Balanced',
  atrLength: 14,
  slBufferAtr: 0.25,
  tp1Mult: 1.0,
  tp2Mult: 2.0,
  tp3Mult: 3.0,
  breakEvenAfterTp1: true,
  // Liquidity
  showRestingLiquidity: true,
  maxLiquidityLevels: 6,
  liquidityLineExtend: 10,
  showEqualHighsLows: true,
  equalToleranceAtr: 0.15,
  showLiquidityTarget: true,
  // Visual
  showRawSweepMarks: true,
  showSweptExtremumLine: true,
  showSweepPenetration: true,
  showSignals: true,
  showScoreLabels: false,
  showSweptLevelLine: true,
  labelFontSize: 'Small',
  // Dashboard
  showDashboard: true,
  dashboardPosition: 'Top Right',
  dashboardFontSize: 'Small',
  showMarketSection: true,
  showTradeSection: true,
  showStatsSection: true,
  // Colors
  bullColor: '#00E676',
  bearColor: '#FF5252',
  levelColor: '#B0BEC5',
  eqColor: '#B0BEC5',
  liqColor: '#90A4AE',
  targetColor: '#FFCA28',
  entryColor: '#5C8AAE',
  slColor: '#E57373',
  tpColor: '#66BB6A',
  labelOffset: 20,
}

export default {
  id: 'liquidity_sweep_pro',
  name: 'Liquidity Sweep Pro',
  version: '1.3.1',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
